import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { CreateOrEditDiscount, capitalize, MomentFormats } from '@getgreenline/homi-shared'
import moment from 'moment'
import { CollapseProps } from 'antd/lib/collapse'
import { SingleCollapsibleContainer } from '../../../../../components/ResponsiveContainer/SingleCollapsibleContainer'
import { ResponsiveRow } from '../../../../../components/ResponsiveContainer/ResponsiveRow'
import { Row, Popconfirm, Button, DatePicker, TimePicker, Checkbox, Radio, Icon } from 'antd'
import ButtonGroup from 'antd/lib/button/button-group'
import { Fade } from 'react-reveal'

interface Props {
  discount: CreateOrEditDiscount
  collapseProps: CollapseProps
  defaultFadeAnimationProps: ReactRevealProps
}

type TRestriction = 'all' | 'range' | 'recurring'

const daysOfTheWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

export const AddDiscountTime = observer<Props>(
  ({ discount, collapseProps, defaultFadeAnimationProps }) => {
    const { applyDateRestriction, isRecurring } = discount

    const [timeSelect, setTimeSelect] = useState(!!discount.startTime && !!discount.endTime)
    const getRestrictionType = (): TRestriction => {
      switch (true) {
        case applyDateRestriction && !isRecurring:
          return 'range'
        case isRecurring:
          return 'recurring'
        default:
          return 'all'
      }
    }
    const [appliedRestriction, setAppliedRestriction] = useState(getRestrictionType())

    useEffect(() => {
      setTimeSelect(!!discount.startTime || !!discount.endTime)
    }, [])

    const resetAllDiscountTimeRestrictions = () => {
      resetDiscountRecurring()
      resetDateRange()
      setTimeSelect(false)
      discount.applyDateRestriction = false
      discount.endTime = null
      discount.startTime = null
    }

    const resetDiscountRecurring = () => {
      discount.isRecurring = false
      daysOfTheWeek.forEach((day) => (discount[day] = false))
    }

    const resetDateRange = () => {
      discount.endDate = null
      discount.startDate = null
    }

    const disabledTime = (time: string | null, greaterThan: boolean, selectedHour?: number) => {
      if (time === null) {
        return []
      }

      // If selected hour is passed we are working out minutes
      const format = selectedHour ? 'mm' : 'HH'
      const parsedHour = parseInt(moment(time, MomentFormats.TimeFormat).format('HH'))

      // If the hours don't match, don't disable minutes
      if (selectedHour && parsedHour !== selectedHour) {
        return []
      }

      const disabledTimeArr: number[] = []
      const parsedTime = parseInt(moment(time, MomentFormats.TimeFormat).format(format))

      if (!greaterThan) {
        for (let index = 0; index < parsedTime; index++) {
          disabledTimeArr.push(index)
        }
      } else {
        for (let index = format === 'HH' ? 24 : 60; index > parsedTime; index--) {
          disabledTimeArr.push(index)
        }
      }

      return disabledTimeArr
    }

    const isRangeAndRecurringSet = () => {
      const isTimeSet = !!(discount.startTime || discount.endTime)

      if (appliedRestriction === 'range') {
        const isRangeSet = !!(discount.startDate || discount.endDate)
        return isRangeSet || isTimeSet
      }

      const isDaySet =
        discount.sunday ||
        discount.monday ||
        discount.tuesday ||
        discount.wednesday ||
        discount.thursday ||
        discount.friday ||
        discount.saturday

      return isTimeSet || isDaySet
    }

    const isEndTimeEarilerThanStartTime = () => {
      const now = moment().format('YYYY-MM-DD')

      if (discount.startTime && discount.endTime) {
        const timeDifferences = moment(`${now} ${discount.endTime}`).diff(
          moment(`${now} ${discount.startTime}`),
          'minutes',
          true,
        )

        return timeDifferences < 0
      }
    }

    const startDate = discount.startDate ? moment(discount.startDate) : undefined
    const endDate = discount.endDate ? moment(discount.endDate) : undefined

    return (
      <SingleCollapsibleContainer
        collapseProps={collapseProps}
        panelProps={{ header: 'Time restriction', key: 'timeRestriction' }}
        defaultActive
      >
        <ResponsiveRow labelProps={{ className: 'mt-0' }} labelText={<strong>Type</strong>}>
          <Radio.Group value={appliedRestriction}>
            {!isRangeAndRecurringSet() ? (
              <Radio
                value='all'
                checked={appliedRestriction === 'all'}
                style={{ display: 'block', marginBottom: 4 }}
                onClick={() => {
                  resetAllDiscountTimeRestrictions()
                  setAppliedRestriction('all')
                }}
              >
                All
              </Radio>
            ) : (
              <Popconfirm
                title='This will remove any current date selection'
                onConfirm={() => {
                  resetAllDiscountTimeRestrictions()
                  setAppliedRestriction('all')
                }}
              >
                <Radio
                  value='all'
                  checked={appliedRestriction === 'all'}
                  style={{ display: 'block', marginBottom: 4 }}
                >
                  All
                </Radio>
              </Popconfirm>
            )}

            <Radio
              value='range'
              checked={appliedRestriction === 'range'}
              onClick={(e) => {
                const value = (e.target as HTMLInputElement).value as TRestriction
                resetDiscountRecurring()
                discount.applyDateRestriction = true
                setAppliedRestriction(value)
              }}
              style={{ display: 'block', marginBottom: 4 }}
            >
              Range
            </Radio>

            <Radio
              value='recurring'
              checked={appliedRestriction === 'recurring'}
              onClick={(e) => {
                const value = (e.target as HTMLInputElement).value as TRestriction
                discount.isRecurring = true
                discount.applyDateRestriction = true
                discount.endDate = null
                discount.startDate = null
                setAppliedRestriction(value)
              }}
              style={{ display: 'block', marginBottom: 4 }}
            >
              Recurring
            </Radio>
          </Radio.Group>

          <div className='caption mb-3'>
            You can make this discount only available at specific times
          </div>

          {appliedRestriction !== 'all' && <hr className='mb-3' />}

          <div className='discount-time'>
            {appliedRestriction === 'range' && (
              <Fade {...defaultFadeAnimationProps}>
                <div className='discount-time-option'>
                  <div className='caption mb-2'>
                    <strong>Date range</strong>
                  </div>

                  <DatePicker.RangePicker
                    defaultValue={startDate && endDate ? [startDate, endDate] : undefined}
                    getCalendarContainer={() => document.body}
                    onChange={(dates, dateStrings) => {
                      discount.startDate = dateStrings[0] || null
                      discount.endDate = dateStrings[1] || null
                    }}
                  />
                </div>
              </Fade>
            )}

            {appliedRestriction === 'recurring' && (
              <Fade {...defaultFadeAnimationProps}>
                <div className='discount-time-option'>
                  <div className='caption mb-2'>
                    <strong>Available days</strong>
                  </div>

                  {daysOfTheWeek.map((d) => (
                    <div key={d} className='discount-time-option-days'>
                      <Checkbox checked={discount[d]} onChange={() => (discount[d] = !discount[d])}>
                        {capitalize(d)}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Fade>
            )}

            {appliedRestriction !== 'all' && (
              <Fade {...defaultFadeAnimationProps}>
                <div className='discount-time-option'>
                  <div className='caption mb-2'>
                    <strong>Time</strong>
                  </div>

                  <ButtonGroup>
                    <Popconfirm
                      title='This will remove the start and end times for this discount'
                      onConfirm={() => {
                        discount.startTime = null
                        discount.endTime = null
                        setTimeSelect(false)
                      }}
                    >
                      <Button type={!timeSelect ? 'primary' : undefined}>All</Button>
                    </Popconfirm>

                    <Button
                      onClick={() => setTimeSelect(true)}
                      type={timeSelect ? 'primary' : undefined}
                    >
                      Specific
                    </Button>
                  </ButtonGroup>
                </div>
              </Fade>
            )}

            {timeSelect && (
              <Fade {...defaultFadeAnimationProps}>
                <div className='discount-time-option'>
                  <Row type='flex'>
                    <div className='mr-3 mb-2'>
                      <div className='caption mb-2'>
                        <strong>Start time</strong>
                      </div>

                      <TimePicker
                        minuteStep={5}
                        use12Hours
                        format='h:mm a'
                        disabledHours={() => disabledTime(discount.endTime, true)}
                        disabledMinutes={(selectedHour) =>
                          disabledTime(discount.endTime, true, selectedHour)
                        }
                        onChange={(time) =>
                          (discount.startTime = time ? time.format(MomentFormats.TimeFormat) : null)
                        }
                        defaultValue={
                          discount.startTime
                            ? moment(discount.startTime, MomentFormats.TimeFormat, true)
                            : undefined
                        }
                      />
                    </div>

                    <div className='mb-2'>
                      <div className='caption mb-2'>
                        <strong>End time</strong>
                      </div>
                      <TimePicker
                        use12Hours
                        format='h:mm a'
                        minuteStep={5}
                        disabledHours={() => disabledTime(discount.startTime, false)}
                        disabledMinutes={(selectedHour) =>
                          disabledTime(discount.startTime, false, selectedHour)
                        }
                        onChange={(time) =>
                          (discount.endTime = time ? time.format(MomentFormats.TimeFormat) : null)
                        }
                        defaultValue={
                          discount.endTime
                            ? moment(discount.endTime, MomentFormats.TimeFormat, true)
                            : undefined
                        }
                      />
                    </div>
                  </Row>

                  {isEndTimeEarilerThanStartTime() && (
                    <div className='caption mb-3' style={{ color: 'var(--red)' }}>
                      <Icon type='info-circle' className='mr-2' />
                      Please set <strong>End time</strong> after <strong>Start time</strong>
                    </div>
                  )}
                </div>
              </Fade>
            )}
          </div>
        </ResponsiveRow>
      </SingleCollapsibleContainer>
    )
  },
)
